/**
 * GeoFenceMultiSelect — chip-style multi-pick dropdown used on the
 * Reports page's filter bar (June 9, 2026 user ask). Surfaces only on
 * Late Comers Review and Absent & Leave Review tabs.
 *
 * Why a bespoke component (vs reusing SearchableSelect):
 *   • Multi-pick UX (checkbox + count badge), not single-select.
 *   • Inline summary "All" / "2 selected" trigger, click anywhere on
 *     the chip to open the picker.
 *   • Type-to-filter inside the open panel (Carloo has 18+ geofences;
 *     scrolling is painful).
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, Search, Check, X } from 'lucide-react';

export default function GeoFenceMultiSelect({ options, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  // Outside-click close.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => (o.name || '').toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (id) => {
    const set = new Set(selectedIds);
    if (set.has(id)) set.delete(id); else set.add(id);
    onChange(Array.from(set));
  };

  const selectAllFiltered = () => {
    const set = new Set(selectedIds);
    for (const o of filtered) set.add(o.id);
    onChange(Array.from(set));
  };
  const clearAll = () => onChange([]);

  const triggerLabel = selectedIds.length === 0
    ? 'All Geo-Fences'
    : selectedIds.length === 1
      ? (options.find(o => o.id === selectedIds[0])?.name || '1 selected')
      : `${selectedIds.length} selected`;

  return (
    <div ref={wrapRef} className="relative" style={{ minWidth: 200 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 border rounded-lg text-sm bg-white text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
        data-testid="reports-geo-multiselect"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate ${selectedIds.length === 0 ? 'text-gray-500' : 'text-gray-900 font-medium'}`}>
          {triggerLabel}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selectedIds.length > 0 && (
            <span className="bg-blue-100 text-blue-700 text-[10px] font-semibold rounded-full px-1.5 py-0.5">
              {selectedIds.length}
            </span>
          )}
          <ChevronDown size={14} className={`text-gray-400 transition ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg overflow-hidden"
          style={{ minWidth: 240 }}
          data-testid="reports-geo-multiselect-menu"
        >
          <div className="px-2 py-1.5 border-b bg-gray-50 flex items-center gap-1.5">
            <Search size={13} className="text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter geo-fences…"
              className="w-full bg-transparent text-sm focus:outline-none placeholder:text-gray-400"
              data-testid="reports-geo-multiselect-search"
            />
            {query && (
              <X
                size={13}
                className="text-gray-400 cursor-pointer hover:text-gray-700"
                onClick={() => setQuery('')}
              />
            )}
          </div>

          <div className="px-2 py-1 border-b bg-gray-50/60 flex items-center justify-between text-[11px]">
            <button
              type="button"
              onClick={selectAllFiltered}
              disabled={filtered.length === 0}
              className="text-blue-600 hover:underline disabled:opacity-40 disabled:no-underline"
              data-testid="reports-geo-multiselect-select-all"
            >
              Select all{query ? ` (${filtered.length})` : ''}
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={selectedIds.length === 0}
              className="text-gray-600 hover:underline disabled:opacity-40 disabled:no-underline"
              data-testid="reports-geo-multiselect-clear"
            >
              Clear
            </button>
          </div>

          <ul role="listbox" className="overflow-y-auto py-1" style={{ maxHeight: 320 }}>
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-xs text-gray-500 text-center">No matches</li>
            )}
            {filtered.map(o => {
              const isSelected = selectedIds.includes(o.id);
              return (
                <li
                  key={o.id}
                  onClick={() => toggle(o.id)}
                  className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 hover:bg-blue-50 ${
                    isSelected ? 'bg-blue-50/60' : 'bg-white'
                  }`}
                  data-testid={`reports-geo-multiselect-opt-${o.id}`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span
                    className={`inline-flex items-center justify-center w-4 h-4 border rounded ${
                      isSelected
                        ? 'bg-blue-600 border-blue-600'
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    {isSelected && <Check size={11} className="text-white" />}
                  </span>
                  <span className={`flex-1 truncate ${isSelected ? 'font-semibold text-blue-900' : 'text-gray-800'}`}>
                    {o.name}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
